import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  InputAdornment,
  Card,
  CardMedia,
  CardContent,
  SelectChangeEvent,
  Table,
  TableContainer,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PhotoCamera,
  RadioButtonChecked
} from "@mui/icons-material";
import { callApi } from "../../../utils/apiHelper";
import { apiEndpoints } from "../../../utils/apiEndpoints";
import {
  Sku,
  RawMaterial,
  UnitConversion,
  UnitType,
  unitCategoryMap,
  standardConversions,
  UnitCategory,
} from "../../../utils/commonApis";
import UnitConverter from "../../UnitConverter";

interface CreateEditSkuProps {
  mode: "create" | "edit";
  sku: Sku | null;
  rawMaterials: RawMaterial[];
  onSubmitSuccess: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sku-tabpanel-${index}`}
      aria-labelledby={`sku-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending" },
  { value: "draft", label: "Draft" },
];

const priorityOptions = [
  { value: 1, label: "1 - Highest" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5 - Lowest" },
];

const CreateEditSku: React.FC<CreateEditSkuProps> = ({
  mode,
  sku,
  rawMaterials,
  onSubmitSuccess,
}) => {
  const [formData, setFormData] = useState<{
    SkuId?: string;
    Code: string;
    Name: string;
    Description: string;
    Status: string;
    BaseUnit: UnitType;
    Priority: number;
    NewRelease: boolean;
    Images: string[];
    Price: number;
    Attributes: Record<string, any>;
    Units: Record<string, number>;
    SkuPerPieceLimit: Record<string, number>;
    UnitConversions: UnitConversion[];
    ParentSkuId?: string;
    Visibility: boolean;
  }>({
    Code: "",
    Name: "",
    Description: "",
    Status: "active",
    BaseUnit: "Pcs" as UnitType,
    Priority: 3,
    NewRelease: false,
    Images: [],
    Price: 0,
    Attributes: {},
    Units: {},
    SkuPerPieceLimit: {},
    UnitConversions: [],
    Visibility: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [materialSearchTerm, setMaterialSearchTerm] = useState("");
  const [filteredMaterials, setFilteredMaterials] = useState<RawMaterial[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<RawMaterial | null>(
    null
  );
  const [materialQuantity, setMaterialQuantity] = useState<number>(0);
  const [materialUnit, setMaterialUnit] = useState<string>("");
  const [materialBaseQuantity, setMaterialBaseQuantity] = useState<number>(0);

  // Initialize form data with SKU data if in edit mode
  useEffect(() => {
    if (mode === "edit" && sku) {
      setFormData({
        SkuId: sku.SkuId,
        Code: sku.Code || "",
        Name: sku.Name || "",
        Description: sku.Description || "",
        Status: sku.Status || "active",
        BaseUnit: sku.BaseUnit || "Pcs" as UnitType,
        Priority: sku.Priority || 3,
        NewRelease: sku.NewRelease || false,
        Images: sku.Images || [],
        Price: sku.Price || 0,
        Attributes: sku.Attributes || {},
        Units: sku.Units || {},
        SkuPerPieceLimit: sku.SkuPerPieceLimit || {},
        UnitConversions: sku.UnitConversions || [],
        ParentSkuId: sku.ParentSkuId || "",
        Visibility: typeof sku.Visibility === "boolean" ? sku.Visibility : true,
      });
    }
  }, [mode, sku]);

  // Filter materials when search term changes
  useEffect(() => {
    if (materialSearchTerm.trim() === "") {
      setFilteredMaterials([]);
      return;
    }

    const term = materialSearchTerm.toLowerCase();
    const filtered = rawMaterials.filter(
      (material) =>
        material.Name.toLowerCase().includes(term) ||
        material.Code.toLowerCase().includes(term)
    );
    setFilteredMaterials(filtered);
  }, [materialSearchTerm, rawMaterials]);

  // Handle form field changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  const handleSwitchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const { name } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Image handling functions
  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target?.result) {
          const dataUrl = event.target.result as string;
          setImagePreview(dataUrl);
        }
      };
      
      reader.readAsDataURL(file);
    }
  };

  const handleConfirmImage = () => {
    if (imagePreview) {
      setFormData((prev) => ({
        ...prev,
        Images: [...prev.Images, imagePreview],
      }));
      setImagePreview(null);
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      Images: prev.Images.filter((_, i) => i !== index),
    }));
  };

  const handleCancelImage = () => {
    setImagePreview(null);
  };

  // Unit conversion handling
  const handleAddUnitConversion = (conversion: UnitConversion) => {
    setFormData((prev) => ({
      ...prev,
      UnitConversions: [...prev.UnitConversions, conversion],
    }));
  };

  // Material limit handling
  const handleMaterialSearch = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setMaterialSearchTerm(e.target.value);
  };

  const handleSelectMaterial = (material: RawMaterial) => {
    setSelectedMaterial(material);
    setMaterialSearchTerm("");
    setFilteredMaterials([]);
    setMaterialUnit(material.BaseUnit);
    setMaterialQuantity(0);
  };

  const handleMaterialQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = parseFloat(e.target.value) || 0;
    setMaterialQuantity(value);
    
    // Convert to base unit if necessary
    if (selectedMaterial && materialUnit !== selectedMaterial.BaseUnit) {
      // Find the category for the material's base unit
      const baseCategory = unitCategoryMap[selectedMaterial.BaseUnit as UnitType];
      
      // Check if selected unit is a standard compatible unit
      if (standardConversions[baseCategory][materialUnit]) {
        const baseValue = value / standardConversions[baseCategory][materialUnit];
        setMaterialBaseQuantity(baseValue);
      } else {
        // Check if it's a custom unit conversion
        const customConversion = selectedMaterial.UnitConversions?.find(
          (conv) => conv.unit === materialUnit
        );
        
        if (customConversion) {
          const baseValue = value / customConversion.conversionFactor;
          setMaterialBaseQuantity(baseValue);
        } else {
          // Default: assume it's the base unit
          setMaterialBaseQuantity(value);
        }
      }
    } else {
      // Using base unit already
      setMaterialBaseQuantity(value);
    }
  };

  const handleMaterialUnitChange = (e: SelectChangeEvent) => {
    const newUnit = e.target.value;
    setMaterialUnit(newUnit);
    
    // Recalculate base quantity when unit changes
    if (selectedMaterial) {
      if (newUnit === selectedMaterial.BaseUnit) {
        // If using base unit, it's a direct value
        setMaterialBaseQuantity(materialQuantity);
      } else {
        // Find the category for the material's base unit
        const baseCategory = unitCategoryMap[selectedMaterial.BaseUnit as UnitType];
        
        // Check if selected unit is a standard compatible unit
        if (standardConversions[baseCategory][newUnit]) {
          const baseValue = materialQuantity / standardConversions[baseCategory][newUnit];
          setMaterialBaseQuantity(baseValue);
        } else {
          // Check if it's a custom unit conversion
          const customConversion = selectedMaterial.UnitConversions?.find(
            (conv) => conv.unit === newUnit
          );
          
          if (customConversion) {
            const baseValue = materialQuantity / customConversion.conversionFactor;
            setMaterialBaseQuantity(baseValue);
          }
        }
      }
    }
  };

  // Get available units for a material
  const getAvailableUnits = (material: RawMaterial | null) => {
    if (!material) return [];
    
    const units = [];
    
    // Add base unit
    units.push(material.BaseUnit);
    
    // Add standard compatible units based on the base unit's category
    const baseCategory = unitCategoryMap[material.BaseUnit as UnitType];
    if (baseCategory) {
      for (const unit in standardConversions[baseCategory]) {
        if (unit !== material.BaseUnit) {
          units.push(unit);
        }
      }
    }
    
    // Add custom unit conversions if available
    if (material.UnitConversions) {
      for (const conversion of material.UnitConversions) {
        if (!units.includes(conversion.unit)) {
          units.push(conversion.unit);
        }
      }
    }
    
    return units;
  };

  const handleAddMaterialLimit = () => {
    if (!selectedMaterial || materialBaseQuantity <= 0) return;
    
    setFormData((prev) => {
      const newLimits = { ...prev.SkuPerPieceLimit };
      newLimits[selectedMaterial.RawMaterialId] = materialBaseQuantity;
      
      return {
        ...prev,
        SkuPerPieceLimit: newLimits,
      };
    });
    
    // Reset material selection
    setSelectedMaterial(null);
    setMaterialQuantity(0);
    setMaterialUnit("");
    setMaterialBaseQuantity(0);
  };

  const handleRemoveMaterialLimit = (materialId: string) => {
    setFormData((prev) => {
      const newLimits = { ...prev.SkuPerPieceLimit };
      delete newLimits[materialId];
      
      return {
        ...prev,
        SkuPerPieceLimit: newLimits,
      };
    });
  };

  // Get material name by ID
  const getMaterialName = (id: string) => {
    const material = rawMaterials.find((m) => m.RawMaterialId === id);
    return material ? material.Name : id;
  };

  // Get material base unit by ID
  const getMaterialBaseUnit = (id: string) => {
    const material = rawMaterials.find((m) => m.RawMaterialId === id);
    return material ? material.BaseUnit : "";
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const endpoint = apiEndpoints.NulookSkuManagement;
      const payload = {
        operation: mode === "create" ? "create" : "update",
        ...formData,
      };
      
      const response = await callApi(endpoint, "POST", payload);
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      setSuccess(true);
      onSubmitSuccess();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="SKU form tabs">
          <Tab label="Basic Information" />
          <Tab label="Images" />
          <Tab label="Units & Conversions" />
          <Tab label="Material Limits" />
        </Tabs>
      </Box>
      
      {/* Basic Information Tab */}
      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SKU Code"
              name="Code"
              value={formData.Code}
              onChange={handleChange}
              required
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SKU Name"
              name="Name"
              value={formData.Name}
              onChange={handleChange}
              required
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Status</InputLabel>
              <Select
                name="Status"
                value={formData.Status}
                onChange={handleSelectChange}
                label="Status"
                required
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Priority</InputLabel>
              <Select
                name="Priority"
                value={formData.Priority.toString()}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    Priority: parseInt(e.target.value),
                  }));
                }}
                label="Priority"
              >
                {priorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Price"
              name="Price"
              type="number"
              value={formData.Price}
              onChange={handleNumberChange}
              required
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Base Unit</InputLabel>
              <Select
                name="BaseUnit"
                value={formData.BaseUnit}
                onChange={handleSelectChange}
                label="Base Unit"
                required
              >
                {Object.keys(unitCategoryMap).map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.NewRelease}
                  onChange={handleSwitchChange}
                  name="NewRelease"
                  color="primary"
                />
              }
              label="New Release"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.Visibility}
                  onChange={handleSwitchChange}
                  name="Visibility"
                  color="primary"
                />
              }
              label="Visible"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="Description"
              value={formData.Description}
              onChange={handleChange}
              multiline
              rows={4}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Images Tab */}
      <TabPanel value={currentTab} index={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                SKU Images
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload images for this SKU. The first image will be used as the primary image.
              </Typography>
              
              <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<PhotoCamera />}
                >
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleAddImage}
                  />
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {imagePreview && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Image Preview
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      mb: 2,
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleConfirmImage}
                    >
                      Add Image
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelImage}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Current Images ({formData.Images.length})
            </Typography>
            {formData.Images.length === 0 ? (
              <Typography variant="body1" color="text.secondary">
                No images added yet
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {formData.Images.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={image}
                        alt={`SKU Image ${index + 1}`}
                      />
                      <CardContent sx={{ p: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2">
                            {index === 0 && (
                              <Chip
                                label="Primary"
                                color="primary"
                                size="small"
                                icon={<RadioButtonChecked />}
                              />
                            )}
                          </Typography>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveImage(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Units & Conversions Tab */}
      <TabPanel value={currentTab} index={2}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Unit Conversions
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Define how the base unit ({formData.BaseUnit}) converts to other units
          </Typography>
          
          <UnitConverter
            mode="add"
            baseUnit={formData.BaseUnit as UnitType}
            baseValue={1}
            unitConversions={formData.UnitConversions}
            onAddConversion={handleAddUnitConversion}
          />
        </Paper>
        
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Unit Converter Demo
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Test your unit conversions
          </Typography>
          
          <UnitConverter
            mode="convert"
            baseUnit={formData.BaseUnit as UnitType}
            baseValue={1}
            unitConversions={formData.UnitConversions}
          />
        </Paper>
      </TabPanel>
      
      {/* Material Limits Tab */}
      <TabPanel value={currentTab} index={3}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Raw Material Consumption Limits
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Define how much of each raw material is needed to produce one unit of this SKU
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Search Raw Materials"
                value={materialSearchTerm}
                onChange={handleMaterialSearch}
                variant="outlined"
                size="small"
              />
              {filteredMaterials.length > 0 && (
                <Paper
                  sx={{
                    mt: 1,
                    maxHeight: "200px",
                    overflow: "auto",
                    position: "absolute",
                    zIndex: 10,
                    width: "calc(100% - 16px)",
                  }}
                >
                  {filteredMaterials.map((material) => (
                    <Box
                      key={material.RawMaterialId}
                      sx={{
                        p: 1.5,
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      }}
                      onClick={() => handleSelectMaterial(material)}
                    >
                      <Typography variant="body1">{material.Name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Code: {material.Code} | Type: {material.RawMaterialType} | Base Unit: {material.BaseUnit}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Grid>
            
            {selectedMaterial && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={materialQuantity}
                    onChange={handleMaterialQuantityChange}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={materialUnit}
                      onChange={handleMaterialUnitChange}
                      label="Unit"
                    >
                      {getAvailableUnits(selectedMaterial).map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddMaterialLimit}
                    disabled={materialBaseQuantity <= 0}
                  >
                    Add
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
        
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Material Consumption Limits
          </Typography>
          
          {Object.keys(formData.SkuPerPieceLimit).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No material limits added yet
            </Typography>
          ) : (
            <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px 16px' }}>Raw Material</th>
                    <th style={{ padding: '8px 16px' }}>Quantity</th>
                    <th style={{ padding: '8px 16px' }}>Base Unit</th>
                    <th style={{ padding: '8px 16px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(formData.SkuPerPieceLimit).map(([materialId, quantity]) => (
                    <tr key={materialId} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 16px' }}>{getMaterialName(materialId)}</td>
                      <td style={{ padding: '8px 16px' }}>{quantity}</td>
                      <td style={{ padding: '8px 16px' }}>{getMaterialBaseUnit(materialId)}</td>
                      <td style={{ padding: '8px 16px' }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMaterialLimit(materialId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Paper>
      </TabPanel>
      
      {/* Form Actions */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        {error && (
          <Typography variant="body2" color="error" sx={{ mr: 2 }}>
            {error}
          </Typography>
        )}
        
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : mode === "create" ? (
            "Create SKU"
          ) : (
            "Update SKU"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateEditSku;